import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthAPI } from "../api";
import ConfirmModal from "../components/ConfirmModal";
import { useAuth } from "../context/AuthContext";

const MyBookings = () => {
  const { isValidating, user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const confirmedSessions = useMemo(() => sessions
    .filter((s) => s.status === "booked")
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time)), [sessions]);

  const completedSessions = useMemo(() => sessions.filter((s) => s.status === "completed"), [sessions]);
  const cancelledSessions = useMemo(() => sessions.filter((s) => s.status === "cancelled"), [sessions]);
  const isMentor = user?.is_mentor === true;

  const loadSessions = useCallback(async () => {
    setError(false);
    setLoading(true);
    try {
      const res = await AuthAPI.get("sessions/");
      setSessions(res.data);
    } catch (err) {
      console.error("Error fetching sessions", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isValidating) return;
    loadSessions();
  }, [isValidating, loadSessions]);

  const handleCancel = async (sessionId) => {
    try {
      await AuthAPI.delete(`sessions/${sessionId}/`);
      setSessions(sessions.filter((s) => s.id !== sessionId));
      showToast("Session cancelled successfully.");
    } catch (err) {
      console.error("Cancel failed", err.response?.data || err.message);
      showToast("Failed to cancel session. Please try again.", "error");
    }
  };

  const formatDateTime = (iso) =>
    new Date(iso).toLocaleString([], {
      weekday: "short", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

  const getCounterpart = (session) => (isMentor ? session.student : session.mentor);
  const totalSessions = sessions.length;
  const nextSession = confirmedSessions[0] || null;

  if (loading || isValidating) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="h-8 w-8 text-[#282E6A] animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <div className="rounded-[28px] border border-red-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600">
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008zm8.25-3.75a8.25 8.25 0 11-16.5 0 8.25 8.25 0 0116.5 0z" />
            </svg>
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-[#111425]">Bookings did not load</h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-[#5d647f]">
            The page could not reach the session feed. Retry before assuming your schedule is empty.
          </p>
          <button
            onClick={loadSessions}
            className="mt-5 inline-flex items-center justify-center rounded-xl bg-[#282E6A] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#1e2458]"
          >
            Retry bookings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 sm:px-6">
      <ConfirmModal
        isOpen={confirmOpen}
        title="Cancel session"
        message="Are you sure you want to cancel this session? This action cannot be undone."
        onConfirm={() => {
          handleCancel(selectedSessionId);
          setConfirmOpen(false);
          setSelectedSessionId(null);
        }}
        onCancel={() => {
          setConfirmOpen(false);
          setSelectedSessionId(null);
        }}
      />

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium ${
          toast.type === "success"
            ? "bg-green-50 text-green-800 border border-green-200"
            : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          {toast.type === "success" ? (
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          {toast.message}
        </div>
      )}

      <section className="overflow-hidden rounded-[30px] border border-[#dfe4f2] bg-white/85 shadow-[0_28px_70px_-48px_rgba(23,30,67,0.45)] backdrop-blur-sm">
        <div className="grid gap-6 px-6 py-7 lg:grid-cols-[1.08fr_0.92fr] lg:px-8">
          <div>
            <span className="inline-flex rounded-full border border-[#cfd5ee] bg-[#eef0fa] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#282E6A]">
              Booking overview
            </span>
            <h1 className="mt-4 text-3xl leading-tight text-[#111425] sm:text-4xl">
              {isMentor ? "Your upcoming mentor sessions, approvals, and history." : "Everything you have booked, completed, or cancelled in one place."}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-[#5d647f] sm:text-base">
              Keep track of live sessions, review what is already done, and cancel upcoming calls before the scheduled time if plans change.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to={isMentor ? "/calendar" : "/mentors"}
                className="inline-flex items-center justify-center rounded-xl bg-[#282E6A] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#1e2458]"
              >
                {isMentor ? "Open calendar" : "Find mentors"}
              </Link>
              <Link
                to="/calendar"
                className="inline-flex items-center justify-center rounded-xl border border-[#cfd5ee] bg-white px-5 py-2.5 text-sm font-medium text-[#282E6A] transition hover:bg-[#eef0fa]"
              >
                View full schedule
              </Link>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
            {[
              { label: "Upcoming", value: confirmedSessions.length, tone: "bg-[#111425] text-white border-[#202640]" },
              { label: "Completed", value: completedSessions.length, tone: "bg-[#f5f1e7] text-[#111425] border-[#ead9b7]" },
              { label: "Total sessions", value: totalSessions, tone: "bg-[#eef0fa] text-[#111425] border-[#d9def3]" },
            ].map((stat) => (
              <div key={stat.label} className={`rounded-[22px] border p-4 ${stat.tone}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">{stat.label}</p>
                <p className="mt-3 text-3xl font-semibold">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {nextSession && (
        <section className="rounded-[28px] border border-[#2e3568] bg-[#1b2146] px-6 py-6 text-white shadow-[0_28px_80px_-52px_rgba(17,20,37,0.75)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Next session</p>
              <h2 className="mt-3 text-2xl leading-tight">
                {getCounterpart(nextSession) || "Scheduled call"}
              </h2>
              <p className="mt-2 text-sm leading-7 text-white/75">
                {formatDateTime(nextSession.start_time)} to {new Date(nextSession.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
            {nextSession.zoom_join_url && (
              <a
                href={nextSession.zoom_join_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-2.5 text-sm font-medium text-[#1b2146] transition hover:bg-[#f3f4fb]"
              >
                Join Zoom
              </a>
            )}
          </div>
        </section>
      )}

      {/* ================= Upcoming ================= */}
      <section className="rounded-[28px] border border-[#dfe4f2] bg-white/92 p-6 shadow-[0_22px_60px_-44px_rgba(23,30,67,0.35)]">
        <h2 className="text-xl font-semibold text-[#111425]">
          Upcoming Sessions
          {confirmedSessions.length > 0 && (
            <span className="ml-2 rounded-full bg-[#eef0fa] px-2.5 py-0.5 text-sm font-normal text-[#282E6A]">
              {confirmedSessions.length}
            </span>
          )}
        </h2>

        {confirmedSessions.length === 0 ? (
          <div className="mt-5 rounded-[22px] border border-dashed border-[#d4daec] bg-[#f7f8fc] px-5 py-8 text-center">
            <p className="text-base font-medium text-[#111425]">No upcoming sessions scheduled</p>
            <p className="mt-2 text-sm text-[#5d647f]">
              {isMentor ? "New student bookings will show up here." : "Book a mentor session when you are ready for clarity."}
            </p>
          </div>
        ) : (
          <ul className="mt-5 space-y-3">
            {confirmedSessions.map((session) => (
              <li
                key={session.id}
                className="flex flex-col gap-4 rounded-[22px] border border-[#e3e6f2] bg-[#fbfbfe] px-4 py-4 lg:flex-row lg:items-start lg:justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-[#111425]">
                    {isMentor ? "Student" : "Mentor"}: {getCounterpart(session)}
                  </p>
                  <p className="mt-1 text-sm text-[#5d647f]">
                    {formatDateTime(session.start_time)} – {new Date(session.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {session.zoom_join_url && (
                    <a
                      href={session.zoom_join_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg bg-[#eef0fa] px-4 py-2 text-sm font-medium text-[#282E6A] transition-colors hover:bg-[#dde0f7]"
                    >
                      Join Zoom
                    </a>
                  )}
                  <button
                    onClick={() => { setSelectedSessionId(session.id); setConfirmOpen(true); }}
                    className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                  >
                    Cancel
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ================= Completed ================= */}
      <section className="rounded-[28px] border border-[#dfe4f2] bg-white/92 p-6 shadow-[0_22px_60px_-44px_rgba(23,30,67,0.35)]">
        <h2 className="text-xl font-semibold text-[#111425]">Completed Sessions</h2>

        {completedSessions.length === 0 ? (
          <div className="mt-5 rounded-[22px] border border-dashed border-[#d4daec] bg-[#f7f8fc] px-5 py-8 text-center">
            <p className="text-base font-medium text-[#111425]">No completed sessions yet</p>
            <p className="mt-2 text-sm text-[#5d647f]">Finished sessions will build your history here.</p>
          </div>
        ) : (
          <ul className="mt-5 space-y-3">
            {completedSessions.map((session) => (
              <li
                key={session.id}
                className="flex flex-col gap-3 rounded-[22px] border border-[#e3e6f2] bg-[#fbfbfe] px-4 py-4 lg:flex-row lg:items-start lg:justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-[#111425]">
                    {isMentor ? "Student" : "Mentor"}: {getCounterpart(session)}
                  </p>
                  <p className="mt-1 text-sm text-[#5d647f]">
                    {formatDateTime(session.start_time)}
                  </p>
                </div>
                <span className="flex-shrink-0 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                  Completed
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ================= Cancelled ================= */}
      {cancelledSessions.length > 0 && (
        <section className="rounded-[28px] border border-[#dfe4f2] bg-white/92 p-6 shadow-[0_22px_60px_-44px_rgba(23,30,67,0.35)]">
          <h2 className="text-xl font-semibold text-[#5d647f]">Cancelled Sessions</h2>
          <ul className="mt-5 space-y-3">
            {cancelledSessions.map((session) => (
              <li
                key={session.id}
                className="flex flex-col gap-3 rounded-[22px] border border-[#eceef6] bg-[#f8f9fc] px-4 py-4 opacity-70 lg:flex-row lg:items-start lg:justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-[#4e556f]">
                    {isMentor ? "Student" : "Mentor"}: {getCounterpart(session)}
                  </p>
                  <p className="mt-1 text-sm text-[#7b8198]">
                    {formatDateTime(session.start_time)}
                  </p>
                </div>
                <span className="flex-shrink-0 rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-600">
                  Cancelled
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default MyBookings;
